import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import confetti from 'canvas-confetti';
import apiService from '../services/ApiService';
import authService from '../services/AuthService';

export default function AchievementToast() {
  const { t } = useTranslation();
  const [newAchievements, setNewAchievements] = useState([]);
  const [open, setOpen] = useState(false);

  const checkNewAchievements = useCallback(() => {
    if (!authService.isAuthenticated()) return;
    apiService
      .get('/api/v1/achievements/my/new')
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setNewAchievements(data);
          setOpen(true);
          confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    checkNewAchievements();
  }, [checkNewAchievements]);

  useEffect(() => {
    window.addEventListener('results-ready', checkNewAchievements);
    return () => window.removeEventListener('results-ready', checkNewAchievements);
  }, [checkNewAchievements]);

  const handleClose = useCallback(() => {
    setOpen(false);
    apiService.post('/api/v1/achievements/my/seen').catch(() => {});
  }, []);

  if (!open || newAchievements.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1a237e 0%, #4a148c 100%)',
          color: 'white',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Typography fontSize="2rem">🏆</Typography>
        <Typography variant="h6" fontWeight={700} color="white">
          {newAchievements.length === 1
            ? t('achievements.newUnlocked')
            : t('achievements.newUnlockedMultiple', { count: newAchievements.length })}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {newAchievements.map((a, i) => (
            <Box key={a.code}>
              {i > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 1.5 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography fontSize="2.5rem">{a.icon}</Typography>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="white">
                    {a.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {a.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={handleClose}
          sx={{
            mt: 2.5,
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white',
            fontWeight: 700,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
          }}
        >
          {t('achievements.awesome')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
